import { Component, OnInit, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Catalog } from '@nousmedis/cc-types';
import { catalogFormSyncWizardFields } from 'app/modules/catalog-cloud/schemas/catalog-form-wizard/catalog-form-wizard-sync.schema';
import { catalogFormWizardFields } from 'app/modules/catalog-cloud/schemas/catalog-form-wizard/catalog-form-wizard.schema';
import { CatalogService } from 'app/modules/catalog-cloud/services/catalog.service';
import { AuthModel } from '@nousmedis/nm-guard-auth';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { User } from '@nousmedis/guard-types/dist/frontend/user.interface';
@Component({
  selector: 'app-catalog-form-wizard',
  templateUrl: './catalog-form-wizard.component.html',
  styleUrl: './catalog-form-wizard.component.scss',
})
export class CatalogFormWizardComponent implements OnInit {
  active = 0;
  isDisabled = false;
  isDisabled2 = true;
  isDisabled3 = true;
  nextButtonDisabled = true;
  loading = false;
  loadingTable = false;

  importData: { label: string; value: any }[] = [];
  showTable = false;
  disableImportButton = false;
  closeButton = true;

  idCatalog = '';
  user: User;

  // Paso 1
  form = new FormGroup({});
  model: any = { extraFields: [] };
  fields: FormlyFieldConfig[] = [];

  // Paso 2
  formSync = new FormGroup({});
  modelSync: any = {};
  fieldsSync: FormlyFieldConfig[] = [];

  constructor(
    private catalogService: CatalogService,
    private auth: AuthModel,
    private messageService: MessageService,
    private translate: TranslateService,
    private router: Router,
    @Optional() private dialogConfig?: DynamicDialogConfig,
    @Optional() private dialogRef?: DynamicDialogRef,
  ) {
    this.user = this.auth.getCurrentUser();
  }

  ngOnInit(): void {
    this.fields = catalogFormWizardFields(this.translate);
    this.fieldsSync = catalogFormSyncWizardFields(this.translate);

    this.model = {
      extraFields: [],
      createdBy: this.user.id,
      updatedBy: this.user.id,
    };

    this.formSync.valueChanges.subscribe(() => {
      this.nextButtonDisabled = true;
    });
  }

  private buildCatalogModel(): Catalog {
    const catalog = this.catalogService.initializeCatalog();
    catalog.id = this.model.id;
    catalog.name = this.model.name;
    catalog.title = this.model.title;
    catalog.createdBy = this.model.createdBy;
    catalog.updatedBy = this.model.updatedBy;
    catalog.extra = (this.model.extraFields || []).map(
      (ef: { key: string; value: string }) => ({
        name: ef.key.replace(/ /g, '_').toLowerCase(),
        label: ef.key,
        value: ef.value,
      })
    ) as any;
    return catalog;
  }

  private buildSyncCatalogModel(): Catalog {
    const catalog = this.catalogService.initializeCatalog();
    catalog.id = this.modelSync.id;
    catalog.name = this.modelSync.name;
    catalog.title = this.modelSync.title;
    catalog.createdBy = this.modelSync.createdBy;
    catalog.updatedBy = this.modelSync.updatedBy;
    catalog.importCredentials = {
      url: this.modelSync.importUrl || '',
      token: this.modelSync.privateImportKey || '',
    };
    catalog.exportCredentials = {
      url: this.modelSync.exportUrl || '',
      token: this.modelSync.privateExportKey || '',
    };
    return catalog;
  }

  createCatalog(): void {
    if (this.form.invalid) {
      console.error('El formulario no es válido.');
      return;
    }

    firstValueFrom(this.catalogService.save(this.buildCatalogModel()))
      .then((res: any) => {
        if (res?.id) {
          this.idCatalog = res.id;
          this.model = { ...this.model, id: res.id };
          this.modelSync = {
            id: res.id,
            name: res.name || '',
            title: res.title || '',
            createdBy: res.createdBy || '',
            updatedBy: res.createdBy || '',
            importUrl: '',
            privateImportKey: '',
            exportUrl: '',
            privateExportKey: '',
          };
          this.isDisabled2 = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Catálogo creado correctamente',
          });
          this.active = 1;
        } else {
          console.error('La respuesta no contiene los datos esperados:', res);
        }
      })
      .catch(() => {});
  }

  async checkConnection(): Promise<void> {
    if (this.formSync.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor, complete los campos requeridos',
      });
      return;
    }

    this.loading = true;
    try {
      const response = await this.catalogService.checkImportExport(this.modelSync);

      if (response.success) {
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Conexión establecida',
        });
        this.isDisabled3 = false;
        this.nextButtonDisabled = false;
      } else {
        if (!response.importCheck) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo establecer conexión con la url de importación, verifique url y credenciales',
          });
          this.isDisabled3 = true;
        }
        if (!response.exportCheck) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo establecer conexión con la url de exportación, verifique url y credenciales',
          });
          this.isDisabled3 = true;
        }
      }
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ocurrió un error inesperado. Inténtelo de nuevo.',
      });
    } finally {
      this.loading = false;
    }
  }

  passNextStep(): void {
    firstValueFrom(this.catalogService.save(this.buildSyncCatalogModel()))
      .then((res) => {
        if (!res) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo actualizar el modelo. Por favor, inténtelo de nuevo.',
          });
          return;
        }
        this.active = 2;
      })
      .catch(() => {});
  }

  importCatalog(): void {
    this.loadingTable = true;
    this.isDisabled = true;
    this.isDisabled2 = true;

    this.catalogService.firstImport(this.idCatalog).subscribe(
      (res: any) => {
        this.importData = [
          { label: 'Categorías', value: res.totalCategories },
          { label: 'Productos', value: res.totalProducts },
          { label: 'Tags', value: res.totalTags },
        ];
        this.showTable = true;
        this.disableImportButton = true;
        this.closeButton = false;
        this.loadingTable = false;
      },
      (error: any) => {
        console.error('Error durante la importación:', error);
      }
    );
  }

  closeModal(): void {
    this.dialogRef?.close();
  }

  goToCatalog(): void {
    this.dialogRef?.close();
    this.router.navigate(['/catalog', this.idCatalog, 'fascicles']);
  }
}
